import type { Theme } from '@mui/material/styles';

/**
 * Mode-aware surface tokens for the queue DataGrids' sticky right
 * "Actions" column and header strips.
 *
 * Sticky cells must stay OPAQUE — they occlude content scrolling beneath
 * them — so hover/tint states are solid per-mode colors, never the
 * translucent `action.hover`. Values are property-level sx callbacks:
 * `backgroundColor: stickySurface` inside any sx block.
 *
 * Dark values form one elevation ladder with the dark palette
 * (canvas #121417 → paper #1c2025 → header strip #1f242a → sticky
 * header #22272d → hover #262c33) so pinned surfaces read as "the same
 * table, slightly raised", not as foreign slabs (the hard-coded whites
 * these replace rendered as glaring white columns in dark mode).
 */
type SurfaceToken = (theme: Theme) => string;

const dark = (theme: Theme) => theme.palette.mode === 'dark';

/** Sticky actions-column cell background (was #ffffff). */
export const stickySurface: SurfaceToken = (t) => t.palette.background.paper;

/** Sticky actions-column header background (was #fafafa). */
export const stickyHeaderSurface: SurfaceToken = (t) => (dark(t) ? '#22272d' : '#fafafa');

/** Sticky actions-column hover background (was #f5f5f5). */
export const stickyHoverSurface: SurfaceToken = (t) => (dark(t) ? '#262c33' : '#f5f5f5');

/** Column-headers strip background (was `grey.50`, which stays light in dark mode). */
export const headerStripSurface: SurfaceToken = (t) => (dark(t) ? '#1f242a' : t.palette.grey[50]);

/** Urgent (red-family) sticky row tint, e.g. `.row-today` / `.row-error` (was #ffebee). */
export const stickyTintUrgent: SurfaceToken = (t) => (dark(t) ? '#382427' : '#ffebee');

/** Urgent tint hover (was #ffcdd2). */
export const stickyTintUrgentHover: SurfaceToken = (t) => (dark(t) ? '#432b2f' : '#ffcdd2');

/** Warning (amber-family) sticky row tint, e.g. `.row-tomorrow` (was #fff8e1). */
export const stickyTintWarning: SurfaceToken = (t) => (dark(t) ? '#37301f' : '#fff8e1');

/** Warning tint hover (was #ffecb3). */
export const stickyTintWarningHover: SurfaceToken = (t) => (dark(t) ? '#423a26' : '#ffecb3');

// ── Dialog / panel surfaces (verification dialogs polish) ────────────────────
// Semantic tinted surfaces used by the packaging / clinical / product
// verification dialogs. Light values preserve the original hard-coded hexes;
// dark values are hand-tinted over the dark paper (#1c2025) so the semantic
// hue (info-blue, success-mint, warning-amber) survives the flip.

/** Plain card / input surface (was 'white' / '#fff'). */
export const surfacePaper: SurfaceToken = (t) => t.palette.background.paper;

/** Neutral raised panel (was `grey.100` / #f5f5f0). */
export const panelNeutral: SurfaceToken = (t) => (dark(t) ? '#232930' : t.palette.grey[100]);

/** Neutral panel hover (was `grey.200`). */
export const panelNeutralHover: SurfaceToken = (t) => (dark(t) ? '#2a3138' : t.palette.grey[200]);

/** Subtle off-white surface (was #fafbfc). */
export const surfaceSubtle: SurfaceToken = (t) => (dark(t) ? '#1a2027' : '#fafbfc');

/** Info (blue-family) tint (was #f0f7ff). */
export const tintInfo: SurfaceToken = (t) => (dark(t) ? '#152233' : '#f0f7ff');

/** Softer info tint (was #f8fbff). */
export const tintInfoSoft: SurfaceToken = (t) => (dark(t) ? '#16202b' : '#f8fbff');

/** Success (mint) tint (was #f6fef9). */
export const tintSuccess: SurfaceToken = (t) => (dark(t) ? '#16241c' : '#f6fef9');

/** Warning (amber) tint (was #fff8e1). */
export const tintWarning: SurfaceToken = (t) => (dark(t) ? '#2a2413' : '#fff8e1');

/** Softer warm tint (was #fffbf0). */
export const tintWarningSoft: SurfaceToken = (t) => (dark(t) ? '#262013' : '#fffbf0');

/** Warning tint border (was #ffe082). */
export const tintWarningBorder: SurfaceToken = (t) => (dark(t) ? '#5c4d1f' : '#ffe082');

/** Strong warning text (was #e65100 — unreadable on dark tints). */
export const textWarningStrong: SurfaceToken = (t) => (dark(t) ? '#ffb74d' : '#e65100');

/** Progress-bar track (was #e0e0e0). */
export const progressTrack: SurfaceToken = (t) => (dark(t) ? '#333a42' : '#e0e0e0');

/** Orange-family alert card (was #fff3e0 — PatientAllergiesCard). */
export const tintWarningCard: SurfaceToken = (t) => (dark(t) ? '#292013' : '#fff3e0');

/** Green-family alert card (was #e8f5e9 — PatientAllergiesCard). */
export const tintSuccessCard: SurfaceToken = (t) => (dark(t) ? '#16241c' : '#e8f5e9');

/** Success body text on tinted cards (was #2e7d32 — too dark for dark tints). */
export const textSuccess: SurfaceToken = (t) => (dark(t) ? '#5cb85c' : '#2e7d32');

/** Warning body text on tinted cards (was #ed6c02). */
export const textWarningMed: SurfaceToken = (t) => (dark(t) ? '#ffb74d' : '#ed6c02');
